import { useAppState } from "@/modules/app/useAppContext";
import usePaginate from "@/components/paginate/usePaginate";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useQuery } from "@tanstack/react-query";
import { getSnowflakeJobDefinition } from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { SnowflakeJobDefinition } from "@/pages/query/types";
import { formatCost, formatMilliseconds } from "@/helpers/utils";
import { ExecutionStatusCount } from "@/pages/query/ExecutionStatusCount";
import { Paginate } from "@/components/paginate";
import { useParams } from "react-router-dom";
import ColorInsightTags from "../../../components/tags/ColorInsightTags";

export const TasksList = () => {
  const { workflowId } = useParams();
  const { currency, enableWarehouseSizeRecommendation } = useAppState();
  const { page, pageSize, setPage, updatePageSize } = usePaginate();
  const { startDate, endDate } = useDateFromSearchParams();
  const { data, isFetching } = useQuery({
    queryKey: ["snowflake_jobs", "definition", workflowId],
    queryFn: () =>
      getSnowflakeJobDefinition(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      }),
  });

  const headers = [
    {
      id: 1,
      label: "Task ID",
      uid: "task_id",
      render: (item: SnowflakeJobDefinition) => (
        <span className="al-text-brand al-break-all">{item.task_id}</span>
      ),
    },
    {
      id: 2,
      label: "Avg. Cost",
      uid: "query_cost",
      render: (item: SnowflakeJobDefinition) => <div>{formatCost(item.query_cost, currency)}</div>,
    },
    {
      id: 3,
      label: "Avg. Time",
      uid: "execution_time",
      render: (item: SnowflakeJobDefinition) => (
        <div>{formatMilliseconds(item.execution_time)}</div>
      ),
    },
    {
      id: 4,
      label: "Runs",
      uid: "run_count",
      render: (item: SnowflakeJobDefinition) => <div>{item.run_count}</div>,
    },
    ...(enableWarehouseSizeRecommendation
      ? [
          {
            id: 5,
            label: "Opportunities",
            uid: "insights",
            render: (item: SnowflakeJobDefinition) => (
              <ColorInsightTags tags={item.insights ?? []} showMore />
            ),
          },
        ]
      : []),
    {
      id: 6,
      label: "Status",
      uid: "status",
      render: (item: SnowflakeJobDefinition) => <ExecutionStatusCount {...item} />,
    },
  ];

  return (
    <Stack direction="column" className={"al-mt-4"}>
      <DataTable
        isLoading={isFetching}
        header={headers}
        data={{
          items: data?.definition.slice(page * pageSize, (page + 1) * pageSize) ?? [],
        }}
        idKey={(item: SnowflakeJobDefinition) => item.task_id}
        getRowUrl={(item: SnowflakeJobDefinition) =>
          `/query/workloads/task-details/${item.task_id}?workflowId=${workflowId}&queryHash=${item.hash}`
        }
        getRowState={(item: SnowflakeJobDefinition) => ({ entityName: item.task_id })}
      />
      <Paginate
        isLoading={isFetching}
        itemCount={data?.definition.length || 0}
        numPages={Math.ceil((data?.definition.length || 0) / pageSize)}
        page={page}
        pageSize={pageSize}
        onPageClick={setPage}
        onPageSizeChange={updatePageSize}
        showPageSize
      />
    </Stack>
  );
};
